import { Controller } from "@hotwired/stimulus"

const LENDER_TYPE_LABEL = { big4: "Big4", challenger: "Challenger", neo: "Neo", credit_union: "Credit union" }
const ACTIVE_BTN   = "bg-primary-600 text-white border-primary-600"
const INACTIVE_BTN = "bg-white text-content-secondary border-border hover:border-primary-400 hover:text-content-primary"

export default class extends Controller {
  static targets = ["amount", "termBtn", "results"]
  static values  = { rates: Array }

  connect() {
    this.selectedTerm = 12
    this.calculate()
  }

  selectTerm(event) {
    this.selectedTerm = parseInt(event.currentTarget.dataset.months)
    this.calculate()
  }

  calculate() {
    this.updateTermButtons()

    const raw    = this.amountTarget.value.replace(/,/g, "").trim()
    const amount = parseFloat(raw) || 0
    const term   = this.selectedTerm

    const rows = this.ratesValue
      .filter(r => r.term_months === term)
      .map(r => ({ ...r, interest: amount * r.rate * (term / 12) }))
      .sort((a, b) => b.interest - a.interest)

    this.resultsTarget.innerHTML = rows.length ? rows.map((r, i) => this.renderRow(r, i, amount)).join("") : this.emptyRow()
  }

  updateTermButtons() {
    this.termBtnTargets.forEach(btn => {
      const active = parseInt(btn.dataset.months) === this.selectedTerm
      btn.className = `rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${active ? ACTIVE_BTN : INACTIVE_BTN}`
    })
  }

  renderRow(r, i, amount) {
    const ratePct    = (r.rate * 100).toFixed(2)
    const interest   = this.formatCurrency(r.interest)
    const total      = this.formatCurrency(amount + r.interest)
    const rowBg      = i === 0 ? "bg-emerald-50" : (i % 2 === 1 ? "bg-surface-subtle" : "")
    const rateColor  = r.rate >= 0.05 ? "text-rating-good font-bold" : r.rate >= 0.045 ? "text-rating-good" : "text-content-primary"
    const badge      = i === 0 ? `<span class="ml-1.5 rounded px-1 py-px text-[9px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700">Best</span>` : ""
    const typeLabel  = LENDER_TYPE_LABEL[r.lender_type] ?? r.lender_type
    const typeBadge  = `<span class="ml-1 inline-block rounded-full px-1.5 py-0.5 text-xs font-normal ${this.badgeClass(r.lender_type)}">${typeLabel}</span>`

    return `
      <tr class="${rowBg} border-b border-border last:border-0">
        <td class="px-4 py-3">
          <a href="/lenders/${r.slug}" class="font-semibold text-content-primary hover:text-primary-700">
            ${r.lender}${typeBadge}
          </a>
        </td>
        <td class="px-4 py-3 text-right">
          <span class="font-semibold ${rateColor}">${ratePct}%</span>${badge}
        </td>
        <td class="px-4 py-3 text-right font-semibold ${i === 0 ? "text-rating-good" : "text-content-primary"}">
          ${amount > 0 ? interest : "—"}
        </td>
        <td class="px-4 py-3 text-right text-content-secondary">
          ${amount > 0 ? total : "—"}
        </td>
      </tr>`
  }

  emptyRow() {
    return `<tr><td colspan="4" class="px-4 py-8 text-center text-content-muted">No rates available for this term.</td></tr>`
  }

  formatCurrency(value) {
    if (!isFinite(value)) return "—"
    return "$" + value.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  badgeClass(type) {
    const map = { big4: "bg-purple-100 text-purple-700", challenger: "bg-blue-100 text-blue-700", neo: "bg-green-100 text-green-700", credit_union: "bg-amber-100 text-amber-700" }
    return map[type] ?? "bg-gray-100 text-gray-700"
  }
}
